import { useCallback, useMemo, useState } from 'react';

import { i18n } from '@lingui/core';
import { Trans, msg, t } from '@lingui/macro';
import PropTypes from 'prop-types';
import { Grid } from 'semantic-ui-react';

import ScatterChartWithExportModal from 'components/customer/visualization/scatter-chart/ScatterChartWithExportModal';
import { NoMarginGrid } from 'components/ui/Grid';
import HelpTooltip from 'components/ui/HelpTooltip';
import ButtonGroup from 'components/ui/button/ButtonGroup';
import { SecondaryTabButton } from 'components/ui/button/TabButton';
import ExportAsImage from 'components/ui/button/export-as/ExportAsImage';
import SentimentCell from 'components/ui/cells/SentimentCell';
import { AnalyticsAwareCheckbox } from 'components/ui/inputs/Checkbox';
import {
  ReviewScoreCell,
  reactTableVolumeCellFactory,
} from 'components/ui/table/cells/ReactTableCell';
import ShareOfExtractTableCell from 'components/ui/table/cells/ShareOfExtractTableCell';
import HierarchicalTreemap from 'components/ui/visualization/HierarchicalTreemap';

import { getInterpolatedColor } from 'utils/colors';
import { capitalize } from 'utils/helpers';

import * as svars from 'assets/style/variables';

import ExplorationOntologyTable from './ExplorationOntologyTable';

const EXPLORATION_CHART_HEIGHT = 300;
const EXPLORE_CHART_ID = 'explore-scatter';

const EXPLORATION_MODE_ITEMS = [
  {
    key: 'treemap',
    i18nLabel: msg({ id: 'treemap-view' }),
    value: 'treemap',
  },
  { key: 'table', i18nLabel: msg({ id: 'table-view' }), value: 'table' },
  { key: 'graph', i18nLabel: msg({ id: 'graphical-view' }), value: 'graph' },
];

const getTreemapTooltipElements = (withReviewScore, maxNChunks) => {
  const treemapTooltipElements = [
    {
      key: 'average_sentiment',
      label: t({ id: 'sentiment' }),
      Cell: SentimentCell,
    },
    {
      key: 'n_chunks',
      label: t({ id: 'volume' }),
      Cell: reactTableVolumeCellFactory(maxNChunks),
    },
    {
      key: 'share_of_extracts',
      label: t({ id: 'share-of-extracts' }),
      Cell: ShareOfExtractTableCell,
    },
  ];
  if (withReviewScore) {
    treemapTooltipElements.push({
      key: 'average_score',
      label: t({ id: 'average-score' }),
      Cell: ReviewScoreCell,
    });
  }
  return treemapTooltipElements;
};

function ExplorationModes({
  customTreemapData,
  customTreemapDataLeavesOnly,
  selectedConceptItem,
  ontologyId,
  onSelectConcept,
  conceptLabelFormatter,
  withReviewScore,
}) {
  const [exploration, setExploration] = useState('treemap');
  const [displayLeafConceptsOnly, setDisplayLeafConceptsOnly] = useState(true);
  const toggleDisplayLeafConceptsOnly = useCallback(() => {
    setDisplayLeafConceptsOnly((prev) => !prev);
  }, []);

  const [exportChartModalIsOpen, setExportChartModalIsOpen] = useState(false);

  const toggleExportChartModal = () => {
    setExportChartModalIsOpen(!exportChartModalIsOpen);
  };

  const onScatterChartShapeClick = useCallback(
    (event, target) => {
      if (!exportChartModalIsOpen) {
        onSelectConcept(target.id, true);
      }
      return null;
    },
    [exportChartModalIsOpen, onSelectConcept]
  );
  const [maxNChunks, treemapTooltipElements] = useMemo(() => {
    const maxN = Math.max(...customTreemapData.map((row) => row.n_chunks));
    return [maxN, getTreemapTooltipElements(withReviewScore, maxN)];
  }, [withReviewScore, customTreemapData]);

  return (
    <NoMarginGrid>
      <Grid.Row columns={1}>
        <Grid.Column>
          <ButtonGroup
            as={SecondaryTabButton}
            items={EXPLORATION_MODE_ITEMS}
            onChange={(_, { value }) => setExploration(value)}
            value={exploration}
            style={{ paddingBottom: svars.spaceNormal }}
            testid="explore-switch-view-button"
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row columns={1} style={{ padding: 0 }}>
        <Grid.Column>
          {exploration === 'treemap' && (
            <HierarchicalTreemap
              id={`hierarchical-treemap-${ontologyId}`}
              withReviewScore={withReviewScore}
              parentNodeId={selectedConceptItem ? selectedConceptItem.id : null}
              data={customTreemapData}
              onClick={onSelectConcept}
              height={EXPLORATION_CHART_HEIGHT}
              tooltipSentimentLabel={t({ id: 'sentiment' })}
              tooltipVolumeLabel={t({ id: 'volume' })}
              tooltipShareOfExtractsLabel={t({ id: 'share-of-extracts' })}
              tooltipElements={treemapTooltipElements}
            />
          )}
          {exploration === 'table' && (
            <ExplorationOntologyTable
              key={`treemap-table-${ontologyId}`}
              withReviewScore={withReviewScore}
              selectedRow={selectedConceptItem}
              maxNChunks={maxNChunks}
              onRowClick={onSelectConcept}
              ontologyId={ontologyId}
              height={EXPLORATION_CHART_HEIGHT}
            />
          )}
          {exploration === 'graph' && (
            <NoMarginGrid>
              <Grid.Row style={{ padding: 0 }} verticalAlign="bottom">
                <Grid.Column width={7}>
                  <span
                    style={{
                      fontSize: svars.fontSizeSmaller,
                      fontStyle: 'italic',
                    }}
                  >
                    <Trans id="dashboard-explore.click-on-dot-to-see-details" />
                    .
                  </span>
                </Grid.Column>
                <Grid.Column width={7} floated="right" textAlign="right">
                  <HelpTooltip
                    help={
                      <Trans id="help-message.parent-category-description" />
                    }
                    size="tiny"
                    position="top center"
                    trigger={
                      <AnalyticsAwareCheckbox
                        gaCategory="Customer"
                        gaAction="Analyze - toggle parents nodes"
                        gaLabel={`${displayLeafConceptsOnly}`}
                        clicked={displayLeafConceptsOnly ? 1 : 0}
                        onClick={toggleDisplayLeafConceptsOnly}
                        style={{
                          borderRadius: 0,
                          textAlign: 'initial',
                          fontWeight: svars.fontWeightBase,
                          fontSize: svars.fontSizeSmaller,
                        }}
                        label={capitalize(
                          t({ id: 'display-also-parent-categories' })
                        )}
                        checked={!displayLeafConceptsOnly}
                      />
                    }
                  />
                </Grid.Column>
                <Grid.Column width={2} floated="right" textAlign="right">
                  <ExportAsImage
                    exportId={EXPLORE_CHART_ID}
                    tooltipPosition="top left"
                    onClick={toggleExportChartModal}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <ScatterChartWithExportModal
                    chartId={EXPLORE_CHART_ID}
                    isAnimationActive={false}
                    data={
                      displayLeafConceptsOnly
                        ? customTreemapDataLeavesOnly
                        : customTreemapData
                    }
                    labelFormatter={conceptLabelFormatter}
                    height={EXPLORATION_CHART_HEIGHT}
                    categoryDataKey="id"
                    colorFormatter={(item) =>
                      getInterpolatedColor(item?.average_sentiment)
                    }
                    getRadius={svars.getPercentScatterRadius(
                      'share_of_extracts'
                    )}
                    radiusTooltipFieldKey="share_of_extracts"
                    onShapeClick={onScatterChartShapeClick}
                    getOpacity={(item) =>
                      ((exportChartModalIsOpen || !selectedConceptItem) && 1) ||
                      item.id === selectedConceptItem.id
                        ? 1
                        : 0.3
                    }
                    modalIsOpen={exportChartModalIsOpen}
                    onCloseModal={toggleExportChartModal}
                    exportName={i18n._(
                      EXPLORATION_MODE_ITEMS.find(({ key }) => key === 'graph')
                        .i18nLabel
                    )}
                  />
                </Grid.Column>
              </Grid.Row>
            </NoMarginGrid>
          )}
        </Grid.Column>
      </Grid.Row>
    </NoMarginGrid>
  );
}

ExplorationModes.propTypes = {
  customTreemapData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      average_sentiment: PropTypes.number.isRequired,
      volume: PropTypes.number.isRequired,
      share_of_extracts: PropTypes.number.isRequired,
    })
  ).isRequired,
  customTreemapDataLeavesOnly: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      average_sentiment: PropTypes.number.isRequired,
      volume: PropTypes.number.isRequired,
      share_of_extracts: PropTypes.number.isRequired,
    })
  ).isRequired,
  selectedConceptItem: PropTypes.shape({ id: PropTypes.string.isRequired }),
  ontologyId: PropTypes.string.isRequired,
  onSelectConcept: PropTypes.func.isRequired,
  conceptLabelFormatter: PropTypes.func.isRequired,
  withReviewScore: PropTypes.bool.isRequired,
};

ExplorationModes.defaultProps = {
  selectedConceptItem: null,
};

export default ExplorationModes;
